import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router';
import { FolderKanban, GitBranch, History, Server, Settings, UserRound, UsersRound } from 'lucide-react';

import { ApiStatusChips } from './ApiStatusChips';
import { ThemeToggle } from './ThemeToggle';
import { UserChip } from './UserChip';
import { FiltersProvider } from '../state/FiltersProvider';

/**
 * Domain navigation (F-09…F-23). Icons are lucide-react inline SVGs — the one
 * sanctioned icon dependency (F-16, STYLEGUIDE §8: 24px grid, stroke).
 */
const NAV_ITEMS: Array<{ to: string; label: string; icon: ReactElement }> = [
  { to: '/streams', label: 'Потоки', icon: <GitBranch size={18} aria-hidden="true" /> },
  { to: '/projects', label: 'Все проекты', icon: <FolderKanban size={18} aria-hidden="true" /> },
  { to: '/people', label: 'Исполнители', icon: <UserRound size={18} aria-hidden="true" /> },
  { to: '/teams', label: 'Команды', icon: <UsersRound size={18} aria-hidden="true" /> },
  { to: '/systems', label: 'Системы', icon: <Server size={18} aria-hidden="true" /> },
  { to: '/audit', label: 'Аудит', icon: <History size={18} aria-hidden="true" /> },
  { to: '/settings', label: 'Настройки', icon: <Settings size={18} aria-hidden="true" /> },
];

/**
 * The app shell: a sidebar (brand, nav, health chips) + a topbar (theme toggle,
 * user chip) around the routed page. The filters provider wraps EVERY view, so
 * the summary chips and the filter bar share one URL-synced state (F-13/F-14).
 */
export function AppLayout(): ReactElement {
  return (
    <FiltersProvider>
      <div className="shell">
        <aside className="sidebar">
          <div className="sidebar__brand">
            <span className="sidebar__logo" aria-hidden="true">
              П
            </span>
            <span className="sidebar__title">Планы</span>
          </div>
          <nav className="sidebar__nav" aria-label="Основная навигация">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
                }
              >
                <span className="sidebar__icon">{item.icon}</span>
                <span className="sidebar__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="sidebar__footer">
            <ApiStatusChips />
          </div>
        </aside>

        <div className="shell__main">
          <header className="topbar">
            <div className="topbar__spacer" />
            <ThemeToggle />
            <UserChip />
          </header>
          <main className="page">
            <Outlet />
          </main>
        </div>
      </div>
    </FiltersProvider>
  );
}
